<template>
    <div class="StatisticsMarketingLead">
        <v-row :class="!prefilter ? 'pa-4' : 'pa-4 mt-1'">
            <template v-for="(filter, index) in filters">
                <vDaterangePicker
                    v-if="filter.type == 'dateperiod'"
                    :key="`filter-dateperiod-${index}`"
                    mode="past"
                    v-model="filter.value"
                    @change="reloadData"
                    class="mr-6"
                    :label="index"
                />
                <v-autocomplete
                    v-else-if="filter.type == 'select' && filter.disabled != true"
                    :key="`filter-select-${index}`"
                    :no-data-text="$t('noData')"
                    multiple
                    clearable
                    v-model="filter.value"
                    class="mr-6 mt-0 pt-0"
                    @change="reloadData"
                    :items="filter.items"
                    style="max-width: 200px;"
                    :label="$t(index)"
                />
                <v-switch
                    v-else-if="filter.type == 'toggle' && filters.entity.value.length"
                    :key="`filter-toggle-${index}`"
                    v-model="filter.value"
                    class="mr-6 mt-0 pt-0"
                    @change="reloadData"
                    :label="$t(index)"
                />
            </template>
        </v-row>

        <!-- Note d'information -->
        <v-alert color="primary" text>
            <template v-slot:prepend>
                <v-icon  color="primary" class="ml-2 mr-4">$info_s</v-icon>
            </template>
            <div v-html="$t('stats_information_note')">
            </div>
        </v-alert>

        <!-- KPIs Cards -->
        <v-row>
            <v-col v-for="(kpi, index) in kpiData" :key="'kpi_' + index">
                <v-card class="kpi-card" :class="kpi.gradient ? 'bg-orange-reverse' : ''" elevation="2">
                    <v-card-text class="pa-4">
                        <div class="kpi-content">
                            <!-- Titre en haut avec ou sans icône info -->
                            <div class="kpi-title-container">
                                <span class="kpi-title">{{ kpi.label }}</span>
                                <infoTooltip v-if="kpi.tooltip" :tooltip="kpi.tooltip" />
                            </div>
                            
                            <!-- Nombre au centre -->
                            <div class="kpi-number">
                                <h2 class="kpi-value">{{ kpi.value }}</h2>
                            </div>
                            
                            <!-- Coût en vert en dessous -->
                            <div class="kpi-cost">
                                <span class="kpi-cost-text">{{ kpi.metric }}</span>
                            </div>
                            
                            <!-- Icône dans cercle bleu clair à droite -->
                            <div class="kpi-icon-container">
                                <div class="kpi-icon-container-inner">
                                    <v-icon :color="kpi.iconColor" size="24">{{ kpi.icon }}</v-icon>
                                    <div class="kpi-icon-circle"></div>
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Section principale avec 4 composants -->
        <v-row v-if="!prefilter">
            <!-- Performance des campagnes - Table -->
            <v-col cols="12">
                <StatisticsTableComponent :filters="filters" :dataUrl="'getCampaignsPerformance'" :titleTable="'campaign_performance'" />
            </v-col>
        </v-row>

        <v-row>
            <!-- Température de leads - Donut Chart -->
            <v-col cols="3" class="d-flex">
                <statisticsDonutComponent :stats="instantData" dataKey="LEAD_TEMPERATURE" :to_trad="true" :colors="temperatureColors" class="fill-height" />
            </v-col>

            <!-- Evolution du nombre de leads générés - Bar Chart -->
            <v-col cols="12" md="6" class="d-flex">
                <statisticsVerticalBarComponent :loadData="loadData" :stats="instantData" dataKey="LEAD_EVOLUTION" class="fill-height" />
            </v-col>

            <!-- Type de leads - Horizontal Bar Chart -->
            <v-col cols="3" class="d-flex">
                <statisticsHorizontalBarComponent :tooltip="{ title: 'lead_type_tooltip_title', description: 'lead_type_tooltip_description' }" :stats="instantData" dataKey="LEAD_TYPE" :to_trad="true" class="fill-height" />
            </v-col>
        </v-row>

        <!-- Section inférieure avec 6 composants -->
        <v-row>
            <!-- Performance des sources - Table -->
            <v-col cols="9">
                <StatisticsTableComponent :filters="filters" :dataUrl="'getSourcesPerformance'" :titleTable="'source_performance'" :utmType="'source'" class="fill-height" />
            </v-col>

            <!-- Leads par source - Donut Chart -->
            <v-col cols="3" class="d-flex">
                <statisticsDonutComponent :stats="instantData" dataKey="LEAD_SOURCE" class="fill-height" />
            </v-col>
        </v-row>

        <v-row>
            <!-- Performance des canaux de diffusion - Table -->
            <v-col cols="9">
                <StatisticsTableComponent :filters="filters" :dataUrl="'getMediumsPerformance'" :titleTable="'medium_performance'" :utmType="'medium'" class="fill-height" />
            </v-col>
            
            <!-- Leads par canal de diffusion - Donut Chart -->
            <v-col cols="3" class="d-flex">
                <statisticsDonutComponent :stats="instantData" dataKey="LEAD_MEDIUM" class="fill-height" />
            </v-col>
        </v-row>

        <v-row>
            <!-- Performance des canaux d'entrée - Table -->
            <v-col cols="9">
                <StatisticsTableComponent :filters="filters" :dataUrl="'getEntriesPerformance'" :titleTable="'entry_performance'" class="fill-height" />
            </v-col>

            <!-- Leads par canal d'entrée - Donut Chart -->
            <v-col cols="3" class="d-flex">
                <statisticsDonutComponent :stats="instantData" dataKey="LEAD_ENTRY" :to_trad="true" class="fill-height" />
            </v-col>
        </v-row>
    </div>
</template>

<script>
import GenericDataService from '@/services/GenericDataService';
import vDaterangePicker from '@/components/forms/inputs/v-daterange-picker.vue';
import statisticsDonutComponent from '@/modules/statistics/components/statisticsDonutComponent.vue'
import statisticsVerticalBarComponent from '@/modules/statistics/components/statisticsVerticalBarComponent.vue'
import statisticsHorizontalBarComponent from '@/modules/statistics/components/statisticsHorizontalBarComponent.vue'
import StatisticsTableComponent from '@/modules/statistics/components/StatisticsTableComponent.vue'
import BarComponent from '@/components/graphs/BarComponent'
import infoTooltip from '@/components/infoTooltip';

export default {
    name: "StatisticsMarketingLead",
    components: {
        statisticsDonutComponent,
        statisticsVerticalBarComponent,
        statisticsHorizontalBarComponent,
        StatisticsTableComponent,
        BarComponent,
        vDaterangePicker,
        infoTooltip,
    },
    props: ['loadData', 'prefilter', 'mode'],
    data() {
        return {
            filters: {},
            instantData: {
                "LEAD_TEMPERATURE": {},
                "LEAD_TYPE": {},
                "LEAD_SOURCE": {},
                "LEAD_MEDIUM": {},
                "LEAD_ENTRY": {},
                "LEAD_EVOLUTION": {},
            },
            kpiData: [],
            temperatureColors: [],
        }
    },
    methods: {
        initFilters() {
            GenericDataService.getData('/stats/getStatisticsMarketingLeadFilters?prefilter=' + JSON.stringify(this.prefilter)).then((response)=> {
                this.filters = response.data.data;

                this.$store.dispatch('SET_LOADER_DASHBOARD', false);

                this.initkpiData();
            });
        },
        reloadData() {
            if(!this.filters.period.value.start || !this.filters.period.value.end) {
                if (!this.prefilter) {
                    // this.filters.period.value = {
                    //     start: new Date(new Date().getFullYear(), new Date().getMonth(), 1, 12, 0, 0).toISOString().slice(0, 10), // Si changement ici il faut répercuter dans le back stats.php dans "getStatisticsFilters"
                    //     end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0, 12, 0, 0).toISOString().slice(0, 10) // Si changement ici il faut répercuter dans le back stats.php dans "getStatisticsFilters"
                    // };
                } else {
                    // this.filters.period.value = {
                    //     start: new Date(new Date(this.prefilter.date_start)).toISOString().slice(0, 10), // Si changement ici il faut répercuter dans le back stats.php dans "getStatisticsFilters"
                    //     end: new Date(new Date()).toISOString().slice(0, 10) // Si changement ici il faut répercuter dans le back stats.php dans "getStatisticsFilters"
                    // };
                }
            }

            this.initkpiData();
        },
        initkpiData() {
            this.kpiData = {};

            const payload = {
                filters: this.filters
            };

            GenericDataService.postData('/stats/getStatisticsMarketingLead', payload).then((response)=> {
                this.kpiData = response.data.data.kpi;
                this.temperatureColors = Object.values(response.data.data.temp).map(item => item.color)
                Object.keys(response.data.data.temp).forEach(item => {
                    this.instantData['LEAD_TEMPERATURE'][item] = response.data.data.temp[item].value;
                });
                this.instantData['LEAD_TYPE'] = response.data.data.type;
                this.instantData['LEAD_SOURCE'] = response.data.data.source;
                this.instantData['LEAD_MEDIUM'] = response.data.data.medium;
                this.instantData['LEAD_ENTRY'] = response.data.data.entry;
                this.instantData['LEAD_EVOLUTION'] = response.data.data.evolution;
            });
        },
    },
    created() {
        this.$store.dispatch('SET_LOADER_DASHBOARD', true).then((e) => {
            this.initFilters();
        });
    }
}
</script>

<style lang="scss" scoped>
.StatisticsMarketingLead {
    .kpi-card {
        height: 100%;

        &.bg-orange-reverse {
            color: white !important;
            
            .v-card-text {
                color: white !important;
            }
            
            .kpi-title {
                color: white !important;
            }
            
            .kpi-value {
                color: white !important;
                font-size: 2.5rem !important;
            }
            
            .kpi-cost-text {
                color: white !important;
            }
            
            .kpi-info-icon {
                color: white !important;
            }
            
            .kpi-icon-circle {
                background-color: rgba(255, 255, 255, 0.2) !important;
            }
        }

        .kpi-content {
            position: relative;
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        }

        .kpi-title-container {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 16px;
        }

        .kpi-number {
            margin-bottom: 16px;
        }

        .kpi-value {
            font-size: 2rem;
            font-weight: bold;
            margin: 0;
            line-height: 1;
            color: #333;
        }

        .kpi-cost {
            margin-bottom: 16px;
        }

        .kpi-cost-text {
            font-size: 0.875rem;
            font-weight: 700;
            color: #4caf50;
        }

        .kpi-icon-container {
            position: absolute;
            top: 50%;
            right: 16px;
            transform: translateY(-50%);
            .v-icon {
                z-index:1;
            }
        }
        .kpi-icon-container-inner{
            position: relative;
            display:flex;
            align-items: center;
            justify-content: center;
            width: 56px;
            height: 56px;
        }
        .kpi-icon-circle {
            position: absolute;
            top:0;
            z-index:0;
            width: 56px;
            height: 56px;
            border-radius: 50%;
            background-color: var(--v-primary-base) !important;
            opacity: 0.2;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .fill-height {
        height: 100% !important;
        width: 100% !important;
    }
}
</style>
